import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { IndianRupee, Download, FileText, Receipt, FileDown } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    api.get('/admin/invoices').then(r => { setInvoices(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateStatus = async (invoiceId, status) => {
    try {
      await api.put(`/admin/invoices/${invoiceId}`, { status });
      setInvoices(prev => prev.map(inv => inv.invoice_id === invoiceId ? { ...inv, status } : inv));
      toast.success('Invoice status updated');
    } catch { toast.error('Failed to update'); }
  };

  const toggleGST = async (inv) => {
    try {
      await api.put(`/admin/invoices/${inv.invoice_id}`, { gst_applied: !inv.gst_applied });
      const res = await api.get('/admin/invoices');
      setInvoices(res.data);
      toast.success(inv.gst_applied ? 'GST removed' : 'GST applied');
    } catch { toast.error('Failed to toggle GST'); }
  };

  const downloadPdf = async (invoiceId) => {
    setDownloading(prev => ({ ...prev, [invoiceId]: true }));
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Download failed');
    }
    setDownloading(prev => ({ ...prev, [invoiceId]: false }));
  };

  const exportCSV = async () => {
    try {
      const res = await api.get('/admin/invoices/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invoices.csv';
      a.click();
      toast.success('Invoices exported');
    } catch { toast.error('Export failed'); }
  };

  const statusColor = (s) => {
    const map = { pending: 'bg-amber-100 text-amber-800', paid: 'bg-green-100 text-green-800', overdue: 'bg-red-100 text-red-800', refunded: 'bg-gray-100 text-gray-800', cancelled: 'bg-gray-100 text-gray-800' };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  const filtered = statusFilter === 'all' ? invoices : invoices.filter(i => i.status === statusFilter);
  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const pendingAmount = invoices.filter(i => i.status === 'pending').reduce((s, i) => s + (i.total || 0), 0);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Invoices</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">{invoices.length} total invoices</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={exportCSV} data-testid="export-invoices-btn">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl border-[#E5E5E0] bg-white" data-testid="invoice-total-card">
          <CardContent className="p-4">
            <p className="text-xs text-[#888]">Total Invoices</p>
            <p className="text-2xl font-bold text-[#2D2D2D]">{invoices.length}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#E5E5E0] bg-white" data-testid="invoice-paid-card">
          <CardContent className="p-4">
            <p className="text-xs text-[#888]">Paid Revenue</p>
            <p className="text-2xl font-bold text-[#4A7C59] flex items-center"><IndianRupee className="w-5 h-5" />{totalRevenue.toFixed(0)}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#E5E5E0] bg-white" data-testid="invoice-pending-card">
          <CardContent className="p-4">
            <p className="text-xs text-[#888]">Pending Amount</p>
            <p className="text-2xl font-bold text-[#E8B931] flex items-center"><IndianRupee className="w-5 h-5" />{pendingAmount.toFixed(0)}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#E5E5E0] bg-white" data-testid="invoice-gst-card">
          <CardContent className="p-4">
            <p className="text-xs text-[#888]">GST Collected</p>
            <p className="text-2xl font-bold text-[#E07A5F] flex items-center"><IndianRupee className="w-5 h-5" />{invoices.reduce((s, i) => s + (i.gst_amount || 0), 0).toFixed(0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-lg" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="rounded-2xl border-[#E5E5E0] bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9F9F7]">
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">Invoice</TableHead>
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">Client</TableHead>
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">Date</TableHead>
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">Subtotal</TableHead>
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">GST</TableHead>
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">Total</TableHead>
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">Status</TableHead>
              <TableHead className="text-xs font-semibold text-[#5A5A5A]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(inv => (
              <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                <TableCell className="text-sm font-mono text-xs">{inv.invoice_id}</TableCell>
                <TableCell className="text-sm">{inv.client_name || '-'}</TableCell>
                <TableCell className="text-sm">{inv.created_at?.split('T')[0]}</TableCell>
                <TableCell className="text-sm">&#8377;{(inv.subtotal || 0).toFixed(2)}</TableCell>
                <TableCell className="text-sm">
                  {inv.gst_applied ? (
                    <span className="text-xs text-[#E07A5F]">&#8377;{(inv.gst_amount || 0).toFixed(2)} ({inv.gst_rate || 0}%)</span>
                  ) : (
                    <button onClick={() => toggleGST(inv)} className="text-xs text-blue-600 underline" data-testid={`apply-gst-${inv.invoice_id}`}>Apply GST</button>
                  )}
                </TableCell>
                <TableCell className="text-sm font-semibold">&#8377;{(inv.total || 0).toFixed(2)}</TableCell>
                <TableCell><span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColor(inv.status)}`}>{inv.status}</span></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Select value={inv.status} onValueChange={(val) => updateStatus(inv.invoice_id, val)}>
                      <SelectTrigger className="w-28 h-7 text-xs rounded-lg" data-testid={`invoice-status-${inv.invoice_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                        <SelectItem value="refunded">Refunded</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => downloadPdf(inv.invoice_id)}
                      disabled={downloading[inv.invoice_id]}
                      className="w-7 h-7 rounded-lg bg-[#F5EDE6] hover:bg-[#E8DDD4] flex items-center justify-center text-[#6A3E28] transition-colors shrink-0"
                      title="Download PDF"
                      data-testid={`download-pdf-${inv.invoice_id}`}
                    >
                      <FileDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
